import { Button, Table, Tag } from "antd"
import type { ColumnsType } from "antd/es/table"
import React from "react"
import { useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { setGlobalBranch } from "../redux/branches.slice.ts"
import type { BranchComparisonRow } from "../types/types.ts"

const money = (value: string) =>
  `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/**
 * One row per branch in the current company-overview scope. "Open
 * workspace" does exactly what picking that branch in the top-header
 * selector does — the same `setGlobalBranch` action — it is a shortcut
 * onto the same mechanism, not a second one.
 */
export const BranchComparisonTable = ({ rows }: { rows: BranchComparisonRow[] }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const openWorkspace = (id: string) => {
    dispatch(setGlobalBranch(id))
    navigate(RoutesPaths.home)
  }

  const columns: ColumnsType<BranchComparisonRow> = [
    {
      title: "Branch",
      dataIndex: "name",
      render: (name: string, row) => (
        <span className="flex items-center gap-2">
          {name}
          {row.is_default_branch && <Tag color="blue">Default</Tag>}
        </span>
      )
    },
    {
      title: "Manager",
      render: (_, row) =>
        row.manager ? `${row.manager.first_name} ${row.manager.last_name}` : "—"
    },
    { title: "Active Leads", dataIndex: "active_leads", align: "center" },
    { title: "Active Projects", dataIndex: "active_projects", align: "center" },
    {
      title: "Delayed",
      dataIndex: "delayed_projects",
      align: "center",
      render: (value: number) =>
        value > 0 ? <Tag color="orange">{value}</Tag> : <span>0</span>
    },
    {
      title: "Outstanding",
      dataIndex: "total_outstanding",
      align: "right",
      render: money
    },
    {
      title: "Overdue",
      dataIndex: "overdue",
      align: "right",
      render: (value: string, row) =>
        row.overdue_count > 0 ? (
          <span className="text-red-600">
            {money(value)} ({row.overdue_count})
          </span>
        ) : (
          money(value)
        )
    },
    {
      title: "",
      key: "action",
      align: "right",
      render: (_, row) => (
        <Button size="small" onClick={() => openWorkspace(row.id)}>
          Open workspace
        </Button>
      )
    }
  ]

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={rows}
      pagination={false}
      size="small"
    />
  )
}
